import React, { useCallback, useEffect, useState } from 'react';
import {
  CalendarDays, CheckCircle2, CornerUpLeft, Eye, MessageSquare, Play, Plus,
  ShieldCheck, Trash2, User, XCircle
} from 'lucide-react';
import {
  Alert, AsyncBoundary, Badge, Button, ConfirmDialog, DiffPreview, Field, Input,
  Modal, SectionTitle, Select, Textarea, Timeline
} from '../ui';
import {
  ACTOR_LABELS, CHANGE_TYPES, EVENT_LABELS, REQUEST_STATUS, adminService,
  canApprove, canExecute, canReject, canReturn, formatDate, formatDateTime
} from '../../services/agencyService';

const changeTypeLabel = (value) => CHANGE_TYPES.find((t) => t.value === value)?.label || 'Otro';

// ⭐ Pantalla de revisión de una solicitud (spec §6 y §7).
//
// Es la pantalla donde vive la regla central de MARKETOS: nada se ejecuta sin
// aprobación previa. Aquí eso se traduce en tres cosas, y las tres importan:
//
// El administrador ve una sola acción principal: aprobar y aplicar. El
// backend conserva la aprobación y la ejecución como eventos separados para
// que la auditoría siga siendo clara, aunque en pantalla sea un solo clic.
export default function SolicitudDetalle({ requestId, open, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [reply, setReply] = useState('');
  const [previewDraft, setPreviewDraft] = useState(null);
  const [manualSummary, setManualSummary] = useState('');

  const load = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await adminService.getRequest(requestId);
      setData(result);
      setPreviewDraft(null);
      setManualSummary('');
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const request = data?.request;
  const status = request ? REQUEST_STATUS[request.status] : null;
  const preview = request?.preview?.items || [];
  const isAutomatedChange = Boolean(
    request?.requested_changes?.kind === 'marketos_managed_change'
    || Array.isArray(request?.requested_changes)
    || Array.isArray(request?.requested_changes?.operations)
  );

  // Toda acción destructiva o de estado pasa por acá: ejecuta, avisa, recarga
  // y notifica al listado padre para que se refresque. Sin duplicar try/catch
  // seis veces.
  const run = async (fn, successMessage) => {
    setBusy(true);
    setNotice(null);
    try {
      const result = await fn();
      // La respuesta de ejecutar ya trae el nuevo estado. Reflejarlo de
      // inmediato evita que el botón verde siga visible durante la recarga
      // posterior o ante una respuesta GET servida desde caché intermedia.
      if (result?.status) {
        setData((current) => current?.request
          ? { ...current, request: { ...current.request, status: result.status } }
          : current);
      }
      setNotice({ tone: 'success', text: successMessage });
      setConfirm(null);
      // La operación ya terminó en el servidor; no mantenemos el botón
      // girando mientras se vuelve a consultar todo el detalle. Esa consulta
      // puede demorarse por otras lecturas del panel y daba la falsa impresión
      // de que una aprobación exitosa seguía pendiente, aun cuando WhatsApp
      // ya había confirmado la finalización.
      onChanged?.();
    } catch (err) {
      setNotice({ tone: 'danger', text: err.message });
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  const askReason = (title, message, confirmLabel, action, defaultReason) => {
    setConfirm({ title, message, confirmLabel, action, reason: defaultReason ?? '', needsReason: true });
  };

  const savePreview = () => run(
    () => adminService.savePreview(requestId, previewDraft.items, previewDraft.impact),
    'Vista previa guardada.'
  );

  const execute = () => run(
    () => adminService.execute(requestId, manualSummary),
    'Cambio ejecutado y registrado en la auditoría.'
  );

  const approveAndExecute = () => run(
    () => adminService.approveAndExecute(requestId, manualSummary),
    'Cambio aprobado, aplicado y notificado.'
  );

  const timelineItems = (data?.events || []).map((event) => ({
    id: event.id,
    title: EVENT_LABELS[event.event_type] || event.event_type,
    description: event.detail?.reason || event.detail?.note || event.detail?.progress_note || null,
    meta: `${ACTOR_LABELS[event.actor_type] || event.actor_type} · ${formatDateTime(event.created_at)}`,
    dotClass: event.event_type === 'execution_completed' ? 'bg-emerald-500'
      : event.event_type === 'rejected' ? 'bg-magenta'
        : event.event_type === 'approved' ? 'bg-gold' : 'bg-navy'
  }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={request ? request.title : 'Solicitud'}
      subtitle={request ? `Solicitud #${request.id} · ${data?.client?.name || ''}` : undefined}
      footer={request && (
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-navy-900/50">
            {canExecute(request)
              ? 'Aprobada. Solo falta aplicarla.'
              : canApprove(request) ? 'Revisa y aprueba; el sistema hará el resto.' : 'Solicitud procesada.'}
          </p>
          <div className="flex flex-wrap gap-2">
            {canReturn(request) && (
              <Button
                variant="ghost" size="sm" icon={<CornerUpLeft className="h-4 w-4" />}
                onClick={() => askReason(
                  'Devolver al cliente',
                  '¿Qué necesitas que aclare el cliente? Lo verá en su portal y podrá responder.',
                  'Devolver', (reason) => adminService.returnToClient(requestId, reason),
                  'Necesitamos que nos aclares un detalle antes de continuar.'
                )}
              >
                Devolver
              </Button>
            )}
            {canReject(request) && (
              <Button
                variant="danger" size="sm" icon={<XCircle className="h-4 w-4" />}
                onClick={() => askReason(
                  'Rechazar solicitud',
                  'Explica al cliente por qué no procede. Quedará registrado.',
                  'Rechazar', (reason) => adminService.reject(requestId, reason),
                  'Necesitamos revisar el alcance antes de ejecutarlo.'
                )}
              >
                Rechazar
              </Button>
            )}
            {canApprove(request) && (
              <Button
                variant="success" size="sm" icon={<CheckCircle2 className="h-4 w-4" />}
                onClick={() => setConfirm({
                  title: 'Aprobar y aplicar',
                  message: 'La IA aplicará el cambio autorizado. Cuando termine, MARKETOS y el cliente recibirán el aviso por WhatsApp.',
                  confirmLabel: 'Aprobar y aplicar',
                  tone: 'success',
                  action: approveAndExecute,
                  isApproveAndExecute: true
                })}
              >
                Aprobar y aplicar
              </Button>
            )}
            {canExecute(request) && (
              <Button
                variant="success" size="sm" icon={<Play className="h-4 w-4" />}
                className="!bg-emerald-600 !text-white shadow-lg ring-2 ring-emerald-200 hover:!bg-emerald-700"
                onClick={() => setConfirm({
                  title: 'Aplicar cambio aprobado',
                  message: 'La IA volverá a intentar el cambio y avisará a ambos cuando termine.',
                  confirmLabel: 'Aplicar ahora',
                  tone: 'success',
                  action: execute,
                  isExecute: true
                })}
              >
                Aplicar cambio
              </Button>
            )}
          </div>
        </div>
      )}
    >
      <AsyncBoundary loading={loading} error={error} onRetry={load} loadingLabel="Cargando solicitud…">
        {request && (
          <div className="space-y-6">
            {notice && <Alert tone={notice.tone} onDismiss={() => setNotice(null)}>{notice.text}</Alert>}

            {/* Cabecera de contexto: los datos que la spec §7 exige tener a la
                vista antes de decidir — quién pidió, a qué producto afecta,
                quién aprobó y cuándo. */}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Meta label="Estado" value={<Badge tone={status?.tone}>{status?.label}</Badge>} />
              <Meta label="Tipo de cambio" value={changeTypeLabel(request.change_type)} />
              <Meta label="Producto afectado" value={data?.service?.name || 'No especificado'} />
              <Meta label="Prioridad" value={request.priority === 'urgent' ? 'Urgente' : request.priority === 'high' ? 'Pronto' : 'Normal'} />
              <Meta label="Solicitó" value={`${ACTOR_LABELS[request.requester_type] || request.requester_type} · ${formatDate(request.created_at)}`} />
              <Meta label="Fecha deseada" value={request.desired_date ? formatDate(request.desired_date) : 'Sin fecha'} icon={<CalendarDays className="h-3.5 w-3.5" />} />
              <Meta label="Aprobó" value={request.approved_at ? `Administrador · ${formatDate(request.approved_at)}` : 'Sin aprobar'} icon={<ShieldCheck className="h-3.5 w-3.5" />} />
              <Meta label="Responsable" value={request.assigned_employee || 'Sin asignar'} icon={<User className="h-3.5 w-3.5" />} />
            </div>

            <div>
              <SectionTitle>Qué pide el cliente</SectionTitle>
              <p className="whitespace-pre-wrap rounded-xl bg-cream p-4 text-sm leading-relaxed text-navy-900/80">
                {request.description}
              </p>
              {request.reason && (
                <p className="mt-2 text-sm text-navy-900/60">
                  <strong className="font-semibold text-navy-900">Motivo:</strong> {request.reason}
                </p>
              )}
            </div>

            {request.returned_reason && request.status === 'returned' && (
              <Alert tone="warning" title="Devuelta al cliente">{request.returned_reason}</Alert>
            )}
            {request.rejected_reason && ['rejected', 'cancelled'].includes(request.status) && (
              <Alert tone="danger" title="Motivo registrado">{request.rejected_reason}</Alert>
            )}

            {/* ─── Vista previa (spec §7) ─── */}
            <div>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <SectionTitle hint="Qué se ve hoy y qué se vería después. Escríbelo como se lo explicarías al cliente.">
                  Vista previa del cambio
                </SectionTitle>
                {!previewDraft && !['completed', 'cancelled'].includes(request.status) && (
                  <Button
                    variant="soft" size="sm" icon={<Eye className="h-4 w-4" />}
                    onClick={() => setPreviewDraft({
                      items: preview.length ? preview.map((i) => ({ ...i })) : [{ label: '', current: '', proposed: '' }],
                      impact: request.preview?.impact || ''
                    })}
                  >
                    {preview.length ? 'Editar vista previa' : 'Preparar vista previa'}
                  </Button>
                )}
              </div>

              {previewDraft ? (
                <PreviewEditor
                  draft={previewDraft}
                  onChange={setPreviewDraft}
                  onCancel={() => setPreviewDraft(null)}
                  onSave={savePreview}
                  saving={busy}
                />
              ) : (
                <>
                  <DiffPreview items={preview} />
                  {request.preview?.impact && (
                    <p className="mt-3 rounded-xl bg-sky-50 px-4 py-3 text-sm text-sky-600">
                      <strong className="font-bold">Impacto estimado:</strong> {request.preview.impact}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Resumen manual: obligatorio cuando la solicitud es trabajo
                humano (diseño, redacción) y no una operación estructurada de
                IA. El backend lo exige; se pide acá para no fallar al pulsar
                Ejecutar y perder el contexto. */}
            {canExecute(request) && !isAutomatedChange && (
              <Field
                label="Resumen del trabajo realizado"
                hint="Se guarda en la auditoría y explica qué se hizo. Obligatorio para trabajo manual."
              >
                <Textarea
                  rows={3}
                  value={manualSummary}
                  onChange={(event) => setManualSummary(event.target.value)}
                  placeholder="Ej.: Se cambió el texto del encabezado y el botón principal ahora enlaza a WhatsApp."
                />
              </Field>
            )}

            <div className="grid gap-6 lg:grid-cols-2">
              <div>
                <SectionTitle hint="Todo movimiento queda registrado. No se puede borrar.">Historial</SectionTitle>
                <Timeline items={timelineItems} />
              </div>

              <div>
                <SectionTitle hint="Lo que escribas aquí lo verá el cliente en su portal.">Mensajes con el cliente</SectionTitle>
                <div className="mb-3 max-h-64 space-y-2 overflow-y-auto">
                  {(data.messages || []).length === 0
                    ? <p className="text-sm text-navy-900/50">Sin mensajes todavía.</p>
                    : data.messages.map((message) => (
                      <div
                        key={message.id}
                        className={`rounded-xl p-3 text-sm ${message.author_type === 'client' ? 'bg-navy-50 text-navy-900' : 'bg-sky-50 text-navy-900'}`}
                      >
                        <p className="mb-1 text-[11px] font-bold uppercase tracking-wide opacity-60">
                          {message.author_type === 'client' ? 'Cliente' : 'MARKETOS'} · {formatDateTime(message.created_at)}
                        </p>
                        <p className="whitespace-pre-wrap">{message.body}</p>
                      </div>
                    ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={reply}
                    onChange={(event) => setReply(event.target.value)}
                    placeholder="Escribe un mensaje para el cliente…"
                  />
                  <Button
                    variant="success"
                    className="!bg-emerald-600 !text-white hover:!bg-emerald-700"
                    icon={<MessageSquare className="h-4 w-4" />}
                    disabled={!reply.trim() || busy}
                    onClick={() => run(
                      async () => { await adminService.sendMessage(request.client_id, reply.trim(), request.id); setReply(''); },
                      'Mensaje enviado al cliente.'
                    )}
                  >
                    Enviar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </AsyncBoundary>

      {/* Confirmación con motivo opcional. Reemplaza a window.confirm/prompt,
          que spec §11 descarta (no se puede traducir ni estilar). */}
      <ConfirmDialog
        open={!!confirm}
        loading={busy}
        title={confirm?.title}
        confirmLabel={confirm?.confirmLabel}
        tone={confirm?.tone || (confirm?.confirmLabel === 'Rechazar' ? 'danger' : 'primary')}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm.isExecute) return execute();
          if (confirm.isApproveAndExecute) return approveAndExecute();
          return run(() => confirm.action(confirm.reason), `${confirm.confirmLabel} correctamente.`);
        }}
        message={
          <span className="block space-y-3">
            <span className="block">{confirm?.message}</span>
            {confirm?.needsReason && (
              <Textarea
                rows={3}
                value={confirm.reason}
                onChange={(event) => setConfirm({ ...confirm, reason: event.target.value })}
                placeholder="Escribe el motivo…"
              />
            )}
          </span>
        }
      />
    </Modal>
  );
}

function Meta({ label, value, icon }) {
  return (
    <div className="rounded-xl border border-navy-50 bg-white px-3 py-2.5">
      <p className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-navy-900/45">
        {icon} {label}
      </p>
      <div className="mt-1 text-sm font-semibold text-navy-900">{value}</div>
    </div>
  );
}

// Editor de la vista previa. Cada fila es "qué campo", "cómo está" y "cómo
// quedaría" — sin jerga técnica, porque el mismo texto lo puede leer el
// cliente si se decide compartírselo.
function PreviewEditor({ draft, onChange, onCancel, onSave, saving }) {
  const setItem = (index, patch) => {
    const items = draft.items.map((item, i) => (i === index ? { ...item, ...patch } : item));
    onChange({ ...draft, items });
  };

  return (
    <div className="space-y-3 rounded-xl border border-dashed border-navy-100 bg-cream p-4">
      {draft.items.map((item, index) => (
        <div key={index} className="rounded-xl border border-navy-50 bg-white p-3">
          <div className="mb-2 flex items-center gap-2">
            <Input
              value={item.label}
              onChange={(event) => setItem(index, { label: event.target.value })}
              placeholder="¿Qué cambia? Ej.: Título de la página de inicio"
            />
            {draft.items.length > 1 && (
              <button
                type="button"
                aria-label="Quitar campo"
                onClick={() => onChange({ ...draft, items: draft.items.filter((_, i) => i !== index) })}
                className="shrink-0 rounded-lg p-2 text-navy-900/40 hover:bg-magenta-50 hover:text-magenta-700"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <Textarea rows={2} value={item.current} onChange={(event) => setItem(index, { current: event.target.value })} placeholder="Cómo está ahora" />
            <Textarea rows={2} value={item.proposed} onChange={(event) => setItem(index, { proposed: event.target.value })} placeholder="Cómo quedaría" />
          </div>
        </div>
      ))}

      <Button
        variant="ghost" size="sm" icon={<Plus className="h-4 w-4" />}
        onClick={() => onChange({ ...draft, items: [...draft.items, { label: '', current: '', proposed: '' }] })}
      >
        Agregar otro campo
      </Button>

      <Field label="Impacto estimado" hint="¿Qué nota el cliente cuando esto se aplique?">
        <Textarea
          rows={2}
          value={draft.impact}
          onChange={(event) => onChange({ ...draft, impact: event.target.value })}
          placeholder="Ej.: La página quedará fuera de línea unos 2 minutos mientras se publica."
        />
      </Field>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>Cancelar</Button>
        <Button variant="secondary" size="sm" onClick={onSave} loading={saving}>Guardar vista previa</Button>
      </div>
    </div>
  );
}

// Reexportado para que la pantalla de listado pueda reutilizar el Select de
// tipos sin volver a importar el vocabulario.
export function ChangeTypeSelect(props) {
  return (
    <Select {...props}>
      <option value="">Todos los tipos</option>
      {CHANGE_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
    </Select>
  );
}
